import { TitleEntry } from '../../../navigation/titleEntry';
import type { EvalIconName } from './evalIcons';

/**
 * The one list of Eval destinations. The sidebar renders it, the mobile topbar
 * takes its label from it, and the browser tab is named from it.
 */

export interface EvalNavItem {
  label: string;
  icon: EvalIconName;
  path: string;
  /** What the browser tab calls this destination, where the sidebar label is too long for it. */
  tabLabel?: string;
}

export interface EvalNavSection {
  title: string;
  items: EvalNavItem[];
}

export const EVAL_NAV_SECTIONS: EvalNavSection[] = [
  {
    title: 'Runs',
    items: [
      { label: 'All Runs', icon: 'ListVideo', path: '/eval/runs' },
      { label: 'Schedule Run', tabLabel: 'Schedule', icon: 'CirclePlus', path: '/eval/runs/new' },
      { label: 'Compare', icon: 'ArrowLeftRight', path: '/eval/runs/compare' },
    ],
  },
  {
    title: 'Pareto',
    items: [
      { label: 'Tasks', icon: 'ClipboardList', path: '/eval/tasks/pareto' },
      { label: 'Error Labels', icon: 'Tags', path: '/eval/error-labels' },
    ],
  },
  {
    title: 'Data',
    items: [
      { label: 'Datasets', icon: 'Folder', path: '/eval/datasets' },
      { label: 'GT Samples', icon: 'Images', path: '/eval/gt-samples' },
      {
        label: 'Import Inference',
        tabLabel: 'Inference import',
        icon: 'Download',
        path: '/eval/pr-inferences',
      },
      {
        label: 'Import PR Samples',
        tabLabel: 'PR import',
        icon: 'CloudUpload',
        path: '/eval/import/pr-evaluation-samples',
      },
    ],
  },
  {
    title: 'Models',
    items: [
      { label: 'OD Models', icon: 'Network', path: '/eval/od-models' },
      { label: 'VE Models', icon: 'Network', path: '/eval/pr-models' },
      { label: 'OOS Models', icon: 'Network', path: '/eval/oos-models' },
      { label: 'PC Configs', icon: 'SlidersHorizontal', path: '/eval/pc-configs' },
    ],
  },
  {
    title: 'Overview',
    items: [{ label: 'Metrics', icon: 'ChartColumn', path: '/eval/metrics' }],
  },
];

export const EVAL_APP_TITLE = 'Eval';

/** Top bar title on mobile: the label of the nav item that owns the path, else 'Eval'. */
export function getEvalPageLabel(pathname: string): string {
  if (/^\/eval\/runs\/[^/]+\/post-processing\/?$/.test(pathname)) {
    return 'Analyse Post Processing';
  }
  const items = EVAL_NAV_SECTIONS.flatMap((section) => section.items);
  const match = items
    .filter((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
  return match?.label ?? EVAL_APP_TITLE;
}

/**
 * Browser tab names for Eval: the sidebar destinations, plus the detail routes
 * that have no sidebar entry of their own. The detail pages replace the id with
 * the record's name once it loads, through `useEntityTitle`.
 */
export const EVAL_TITLE_ENTRIES: TitleEntry[] = [
  ...EVAL_NAV_SECTIONS.flatMap((section) =>
    section.items.map((item) => ({ path: item.path, label: item.tabLabel ?? item.label }))
  ),
  { path: '/eval/runs/:runId', label: 'Run', params: [{ name: 'runId', opaque: true }] },
  {
    path: '/eval/runs/:runId/errors',
    label: 'Error analysis',
    params: [{ name: 'runId', opaque: true }],
  },
  {
    path: '/eval/runs/:runId/subtasks',
    label: 'Subtask analysis',
    params: [{ name: 'runId', opaque: true }],
  },
  { path: '/eval/runs/compare/errors', label: 'Differences analysis' },
  { path: '/eval/runs/compare/subtasks', label: 'Subtask differences analysis' },
  {
    path: '/eval/runs/:runId/post-processing',
    label: 'Analyse Post Processing',
    params: [{ name: 'runId', opaque: true }],
  },
  {
    path: '/eval/runs/:runId/inferences/:runInferenceId',
    label: 'Sample',
    params: [{ name: 'runInferenceId', opaque: true }],
  },
  { path: '/eval/datasets/new', label: 'New dataset' },
  {
    path: '/eval/datasets/:datasetId',
    label: 'Dataset',
    params: [{ name: 'datasetId', opaque: true }],
  },
  {
    path: '/eval/gt-samples/:gtSampleId',
    label: 'GT sample',
    params: [{ name: 'gtSampleId', opaque: true }],
  },
  { path: '/eval/models/:modelId', label: 'Model', params: [{ name: 'modelId', opaque: true }] },
];
